import { useTranslation } from 'react-i18next';
import { Typography, useTheme } from '@mui/material';
import { formatMoneyMinorParts } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens, type FigureVariant } from '@/theme/theme';
import { registerAmountColor } from './moneyTone';

/**
 * How an amount is coloured (visual contract v2 §13, the Architect's ruling of 2026-10-19,
 * TMMAPP-0154 §A 1):
 * - `balance` (default): `text`, or `moneyNegative` when negative — headline and card balances
 *   (never green);
 * - `register`: a register or list amount at weight 500 — outgoing `text` with a true minus,
 *   incoming `moneyPositive` with "+", zero `text2`;
 * - `running`: a running balance at 400 in `text2` (a negative one stays `moneyNegative`);
 * - `total`: a tile or line of totals — `text` (ink) whatever the sign, signed by its own minus;
 * - `signed`: an Unassigned figure (audit 06 § Overrides 7, TMMAPP-0109): positive `moneyPositive`,
 *   zero `text`, negative `moneyNegative`, at the variant's own weight;
 * - `discount`: a promotion (TMMAPP-0128, TMM-B1) — `moneyPositive` even when the amount is negative.
 */
export type MoneyTone = 'balance' | 'register' | 'running' | 'total' | 'signed' | 'discount';

/** The sign's own numerals: proportional, so it sits tight against the tabular digits. */
const SIGN_STYLE = { fontVariantNumeric: 'normal' } as const;
/** The amount as one unbroken unit: a flex parent never splits the sign from the number. */
const UNIT_STYLE = { whiteSpace: 'nowrap' } as const;

interface MoneyPartsProps {
  minor: number;
  currency: string | null | undefined;
  signed?: boolean;
  /** Whole units, for an aggregate (contract v2 §13); cents otherwise. */
  whole?: boolean;
}

function Parts({ minor, currency, signed = false, whole = false }: MoneyPartsProps) {
  const locale = useLocale();
  return (
    <>
      {formatMoneyMinorParts(minor, currency, locale, signed, { whole }).map((part, index) =>
        part.sign ? (
          <span key={index} data-money-sign="" style={SIGN_STYLE}>
            {part.text}
          </span>
        ) : (
          part.text
        ),
      )}
    </>
  );
}

/**
 * An amount's text with the sign in its own proportional span (TMMAPP-0111), and nothing else: it
 * inherits the face, size and colour of where it sits. The one renderer for an amount shown inside
 * another styled node (a register cell, a chip, a table figure); `MoneyText` uses it too
 * (TMMAPP-0113 4d). The sign and the number sit in one `nowrap` span, so a flex parent (a chip with a
 * gap) never splits them (TMMAPP-0154 §A 1). A plain string stays only where the value isn't an
 * amount node: an `aria-label`, a sentence's interpolation, an export.
 */
export function MoneyParts(props: MoneyPartsProps) {
  return (
    <span data-money-unit="" style={UNIT_STYLE}>
      <Parts {...props} />
    </span>
  );
}

interface MoneyTextProps {
  minor: number | null;
  /** `null` when no currency is served: a plain number, never a guessed currency. */
  currency: string | null;
  /**
   * A large figure (`figure*`, TMM-T2 1.4) or a stat value (`stat`); every other amount is
   * working text (`body1`, or `body2` / `caption` beside smaller text).
   */
  variant?: FigureVariant | 'stat' | 'statSm' | 'body1' | 'body2' | 'caption';
  component?: 'span' | 'div';
  struck?: boolean;
  /** Show "+" on a positive amount; a `register` amount always does. */
  signed?: boolean;
  /** Whole units, for an aggregate (contract v2 §13). */
  whole?: boolean;
  tone?: MoneyTone;
}

/**
 * An amount: the body face with tabular numerals, never mono (TMM-T1 / TMM-B1 1.4), right-aligned
 * and unbroken (its root is the unit); a negative reads with a true minus (U+2212). The sign is set
 * proportional (TMMAPP-0111): a tabular minus takes a digit's width and floats away from the amount.
 */
export function MoneyText({
  minor,
  currency,
  variant = 'body1',
  component = 'span',
  struck = false,
  signed = false,
  whole = false,
  tone = 'balance',
}: MoneyTextProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const color = (() => {
    if (struck) return tokens.text2;
    if (minor === null) return tone === 'balance' || tone === 'total' ? tokens.text : tokens.text2;
    if (tone === 'discount') return tokens.moneyPositive;
    if (tone === 'register') return registerAmountColor(tokens, minor);
    if (tone === 'total') return tokens.text;
    if (minor < 0) return tokens.moneyNegative;
    if (tone === 'running') return tokens.text2;
    if (tone === 'signed') return minor > 0 ? tokens.moneyPositive : tokens.text;
    return tokens.text;
  })();
  return (
    <Typography
      role={minor === null ? 'img' : undefined}
      aria-label={minor === null ? t('common.notAvailable') : undefined}
      variant={variant}
      component={component}
      data-money-tone={tone}
      sx={{
        color,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        textDecoration: struck ? 'line-through' : undefined,
        ...(tone === 'register' ? { fontWeight: 500 } : {}),
        ...(tone === 'running' ? { fontWeight: 400 } : {}),
      }}
    >
      {minor === null ? (
        '—'
      ) : (
        <Parts
          minor={minor}
          currency={currency}
          signed={signed || tone === 'register'}
          whole={whole}
        />
      )}
    </Typography>
  );
}
